import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  Plus,
  Search,
  FileText,
  Clock,
  Calendar,
  Eye,
  Pencil,
  Trash2,
  MoreHorizontal,
  BookOpen,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface DeepDive {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  coverImage?: string;
  wordCount: number;
  readTime: number; // in minutes
  status: "published" | "draft" | "scheduled";
  publishedAt?: string;
  scheduledAt?: string;
  createdAt: string;
  updatedAt: string;
  views: number;
  category: string;
}

interface DeepDiveListProps {
  deepDives: DeepDive[];
  isLoading: boolean;
  onView: (deepDive: DeepDive) => void;
  onEdit: (deepDive: DeepDive) => void;
  onDelete: (deepDive: DeepDive) => void;
  onCreate: () => void;
}

type StatusFilter = "all" | "published" | "draft" | "scheduled";

function getStatusBadge(status: DeepDive["status"]): {
  label: string;
  variant: "success" | "warning" | "secondary";
} {
  switch (status) {
    case "published":
      return { label: "Published", variant: "success" };
    case "draft":
      return { label: "Draft", variant: "warning" };
    case "scheduled":
      return { label: "Scheduled", variant: "secondary" };
    default:
      return { label: "Unknown", variant: "secondary" };
  }
}

function DeepDiveCard({
  deepDive,
  onView,
  onEdit,
  onDelete,
  delay,
}: {
  deepDive: DeepDive;
  onView: (deepDive: DeepDive) => void;
  onEdit: (deepDive: DeepDive) => void;
  onDelete: (deepDive: DeepDive) => void;
  delay: number;
}) {
  const status = getStatusBadge(deepDive.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.3, delay: delay * 0.05 }}
    >
      <Card className="group overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-primary/20">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Cover image */}
            <div className="sm:w-48 h-32 sm:h-auto bg-muted shrink-0 overflow-hidden">
              {deepDive.coverImage ? (
                <img
                  src={deepDive.coverImage}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <BookOpen className="h-8 w-8 text-muted-foreground/50" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 p-4 sm:p-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={status.variant} className="text-xs">
                      {status.label}
                    </Badge>
                    <Badge variant="outline" className="text-xs font-normal">
                      {deepDive.category}
                    </Badge>
                  </div>
                  <h3
                    className="font-semibold text-lg leading-tight line-clamp-2 cursor-pointer hover:text-primary transition-colors"
                    onClick={() => onView(deepDive)}
                  >
                    {deepDive.title}
                  </h3>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onView(deepDive)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onEdit(deepDive)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(deepDive)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Excerpt */}
              <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                {deepDive.excerpt}
              </p>

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{deepDive.author}</span>
                <span className="flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />
                  {deepDive.wordCount.toLocaleString()} words
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {deepDive.readTime} min read
                </span>
                {deepDive.status === "published" && (
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    {deepDive.views.toLocaleString()} views
                  </span>
                )}
                {deepDive.status === "scheduled" && deepDive.scheduledAt && (
                  <span className="flex items-center gap-1 text-primary">
                    <Calendar className="h-3.5 w-3.5" />
                    {format(new Date(deepDive.scheduledAt), "MMM d, yyyy 'at' h:mm a")}
                  </span>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <span className="text-xs text-muted-foreground">
                  {deepDive.status === "published" && deepDive.publishedAt
                    ? `Published ${format(new Date(deepDive.publishedAt), "MMM d, yyyy")}`
                    : `Last edited ${format(new Date(deepDive.updatedAt), "MMM d, yyyy")}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onEdit(deepDive)}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function DeepDiveCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row">
          <Skeleton className="sm:w-48 h-32 sm:h-40 rounded-none" />
          <div className="flex-1 p-4 sm:p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex items-center gap-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DeepDiveList({
  deepDives,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onCreate,
}: DeepDiveListProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Filter deep dives
  const filteredDeepDives = deepDives.filter((dd) => {
    const matchesSearch =
      dd.title.toLowerCase().includes(search.toLowerCase()) ||
      dd.author.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || dd.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const publishedCount = deepDives.filter((d) => d.status === "published").length;
  const draftCount = deepDives.filter((d) => d.status === "draft").length;
  const totalWords = deepDives.reduce((sum, d) => sum + d.wordCount, 0);

  return (
    <div className="space-y-6">
      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
      >
        <div className="p-4 rounded-lg bg-muted/50 border">
          <p className="text-2xl font-semibold">{deepDives.length}</p>
          <p className="text-sm text-muted-foreground">Total Deep Dives</p>
        </div>
        <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
            {publishedCount}
          </p>
          <p className="text-sm text-muted-foreground">Published</p>
        </div>
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
            {draftCount}
          </p>
          <p className="text-sm text-muted-foreground">Drafts</p>
        </div>
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <p className="text-2xl font-semibold text-primary">
            {(totalWords / 1000).toFixed(0)}k
          </p>
          <p className="text-sm text-muted-foreground">Total Words</p>
        </div>
      </motion.div>

      {/* Filters and actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search deep dives..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-background"
            />
          </div>

          {/* Status filter */}
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[140px] h-10 bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Create button */}
        <Button onClick={onCreate} className="h-10">
          <Plus className="h-4 w-4 mr-2" />
          New Deep Dive
        </Button>
      </div>

      {/* Deep dive list */}
      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <DeepDiveCardSkeleton key={i} />
          ))
        ) : filteredDeepDives.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-muted-foreground">No deep dives found</h3>
              <p className="text-sm text-muted-foreground/70 mt-1">
                {deepDives.length === 0
                  ? "Create your first deep dive article"
                  : "Try adjusting your filters"}
              </p>
              {deepDives.length === 0 && (
                <Button onClick={onCreate} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Deep Dive
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <AnimatePresence mode="popLayout">
            {filteredDeepDives.map((deepDive, index) => (
              <DeepDiveCard
                key={deepDive.id}
                deepDive={deepDive}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                delay={index}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
